'use client';

import type { FunctionComponentElement, PropsWithChildren} from 'react';
import React, { useMemo } from 'react';

import './Screens.scss';
import { omit } from 'lodash';
import entries from 'lodash/entries';
import orderBy from 'lodash/orderBy';

import { createMedia } from '@artsy/fresnel';
import { type MediaProps } from '@artsy/fresnel/dist/Media';
import { OneOrMany } from '@utilities/helpers/OneOrMany';

import { BREAKPOINTS } from '../../../../../styles/exports/breakpoints';

const EXTENDED_BREAKPOINTS = {
  //  For fresnel to work properly at the smallest breakpoint,
  //  it is required to have the lower bound for it
  SMALLEST: 0,
  ...BREAKPOINTS,
  LARGEST: 9999,
} as const;

const { MediaContextProvider, Media } = createMedia({
  breakpoints: EXTENDED_BREAKPOINTS,
});

const ORDERED_BREAKPOINT_KEYS = orderBy(entries(EXTENDED_BREAKPOINTS), ([key, value]) => value).map(
  ([key]) => key,
) as Array<keyof typeof EXTENDED_BREAKPOINTS>;

const ScreensProvider = ({ children }: PropsWithChildren) => {
  return <MediaContextProvider>{children}</MediaContextProvider>;
};

interface ScreensResponsiveProps {
  children?: OneOrMany<FunctionComponentElement<ScreensBreakpointProps>>;
}

const ScreensResponsive = ({ children }: ScreensResponsiveProps) => {
  const breakpoints = useMemo(() => {
    const orderedBreakpoints = ORDERED_BREAKPOINT_KEYS.map((breakpoint) => {
      return OneOrMany.asArray(children).find((child) => child.props.at === breakpoint) ?? null;
    }).filter((breakpoint) => breakpoint !== null);

    const largestBreakpoint = orderedBreakpoints.pop();

    if (largestBreakpoint) {
      orderedBreakpoints.push(
        React.cloneElement(largestBreakpoint, {
          ...omit(largestBreakpoint.props, 'at'),
          at: undefined,
          greaterThan: largestBreakpoint.props.at,
        }),
      );
    }

    return orderedBreakpoints;
  }, [children]);

  return (
    <ScreensProvider>
      {breakpoints}
    </ScreensProvider>
  );
};

type ScreensBreakpointBaseProps = Pick<MediaProps<any, any>, 'className' | 'style'>;

interface ScreensBreakpointPropsAt extends ScreensBreakpointBaseProps {
  at: keyof typeof BREAKPOINTS;
  greaterThan?: never;
}

interface ScreensBreakpointPropsGreaterThan extends ScreensBreakpointBaseProps {
  at?: never;
  greaterThan: keyof typeof BREAKPOINTS;
}

type ScreensBreakpointProps = PropsWithChildren &
  (ScreensBreakpointPropsAt | ScreensBreakpointPropsGreaterThan);

const ScreensBreakpoint = ({ children, at, greaterThan, ...props }: ScreensBreakpointProps) => {
  return at ? (
    <Media
      at={ORDERED_BREAKPOINT_KEYS[ORDERED_BREAKPOINT_KEYS.indexOf(at) - 1]}
      {...props}
    >
      {children}
    </Media>
  ) : (
    <Media
      greaterThanOrEqual={ORDERED_BREAKPOINT_KEYS[ORDERED_BREAKPOINT_KEYS.indexOf(greaterThan) - 1]}
      {...props}
    >
      {children}
    </Media>
  );
};

const Screens = {
  Provider: ScreensProvider,
  Breakpoint: ScreensBreakpoint,
};

export { Screens, ScreensProvider, ScreensBreakpoint, ScreensResponsive };
